import parseJwt from '../helpers/parse-token'
import action from '../helpers/rest-client'
import { cookie } from '../helpers/cookies'

export class ErrorAuth extends Error { }

class Auth {
  private tokenInformation: any
  private refreshAcessTokenInformation: any
  private refreshAcessToken: any
  private accessToken: any
  private refreshTokenTimeout: any

  public getRole = async () => {
    return this.tokenInformation.roles
  }

  public getToken = () => this.accessToken || this.refreshAcessToken

  public refreshToken = async () => {
    const refreshToken = cookie.get("GASAPP_REFRESH_TOKEN")

    return action
      .Post({
        url: '/auth/refresh-token',
        body: {},
        config: { headers: { authorization: `Bearer ${refreshToken}` } },
      })
      .then(response => {
        this.accessToken = response.accessToken;
        this.tokenInformation = parseJwt(this.accessToken);
        const accessTokenTime = this.tokenInformation.exp * 1000;
        const accessTokenExpiration = new Date(accessTokenTime).toUTCString();
        cookie.set("GASAPP_TOKEN", this.accessToken, accessTokenExpiration);

        this.refreshAcessToken = response.refreshToken;
        this.refreshAcessTokenInformation = parseJwt(this.refreshAcessToken);
        const refreshAccessTokenTime = this.refreshAcessTokenInformation.exp * 1000;
        const refreshAccessTokenExpiration = new Date(refreshAccessTokenTime).toUTCString();
        cookie.set("GASAPP_REFRESH_TOKEN", this.refreshAcessToken, refreshAccessTokenExpiration);

        this.startRefreshTokenTimer();
        return { response: this.tokenInformation }
      })
      .catch(error => {
        return { error }
      })
  }

  public autoSignIn = () => {
    const accessToken = cookie.get("GASAPP_TOKEN");
    const refreshAcessToken = cookie.get("GASAPP_REFRESH_TOKEN");

    if (!!accessToken && !!refreshAcessToken) {
      this.accessToken = accessToken;
      this.tokenInformation = parseJwt(accessToken);

      this.refreshAcessToken = refreshAcessToken;
      this.refreshAcessTokenInformation = parseJwt(this.refreshAcessToken);

      const accessTokenExpired = this.accessTokenExpired();

      if (!accessTokenExpired) {
        this.startRefreshTokenTimer();
        return { response: this.tokenInformation }
      } else {
        const time = this.refreshAcessTokenInformation.exp * 1000;
        const expires = new Date(time);
        let timeout = 0;

        if (expires) {
          timeout = expires.getTime() - Date.now() - 60 * 1000
        }

        if (timeout > 0) {
          return this.refreshToken()
        } else {
          return {
            error: "Token must be renewed."
          }
        }
      }
    } return {
      error: "Token must be renewed."
    }
  }

  public signIn = async payload => {
    return action
      .Post({
        url: '/auth/local/sign-in',
        body: payload,
      })
      .then(response => {
        this.accessToken = response.accessToken;
        this.tokenInformation = parseJwt(this.accessToken);
        const accessTokenTime = this.tokenInformation.exp * 1000;
        const accessTokenExpiration = new Date(accessTokenTime).toUTCString();
        cookie.set("GASAPP_TOKEN", this.accessToken, accessTokenExpiration);

        this.refreshAcessToken = response.refreshToken;
        this.refreshAcessTokenInformation = parseJwt(this.refreshAcessToken);
        const refreshAccessTokenTime = this.refreshAcessTokenInformation.exp * 1000;
        const refreshAccessTokenExpiration = new Date(refreshAccessTokenTime).toUTCString();
        cookie.set("GASAPP_REFRESH_TOKEN", this.refreshAcessToken, refreshAccessTokenExpiration);

        this.startRefreshTokenTimer();
        return { response: this.tokenInformation }
      })
      .catch(error => {
        return { error }
      })
  }

  public signUp = async payload => {
    return action
      .Post({
        url: '/auth/local/sign-up',
        body: payload,
      })
      .then(response => {
        this.accessToken = response.accessToken;
        this.tokenInformation = parseJwt(this.accessToken);
        const accessTokenTime = this.tokenInformation.exp * 1000;
        const accessTokenExpiration = new Date(accessTokenTime).toUTCString();
        cookie.set("GASAPP_TOKEN", this.accessToken, accessTokenExpiration);

        this.refreshAcessToken = response.refreshToken;
        this.refreshAcessTokenInformation = parseJwt(this.refreshAcessToken);
        const refreshAccessTokenTime = this.refreshAcessTokenInformation.exp * 1000;
        const refreshAccessTokenExpiration = new Date(refreshAccessTokenTime).toUTCString();
        cookie.set("GASAPP_REFRESH_TOKEN", this.refreshAcessToken, refreshAccessTokenExpiration);

        this.startRefreshTokenTimer();
        return { response: this.tokenInformation }
      })
      .catch(error => {
        return { error }
      })
  }

  public signOut = async () => {
    return action
      .Post({
        url: '/auth/local/sign-out',
        body: {},
      })
      .then(response => {
        // Detener el timer de refresh
        this.stopRefreshTokenTimer()

        // Limpiar variables de instancia
        this.accessToken = null
        this.refreshAcessToken = null
        this.tokenInformation = null
        this.refreshAcessTokenInformation = null
        this.refreshTokenTimeout = null

        // Limpiar cookies de manera completa
        this.clearAllTokens()

        // Nota: El reset del currentUser se maneja en el componente SignOut
        // para mantener la separación de responsabilidades

        return { response }
      })
      .catch(error => {
        // Aún así limpiar tokens locales en caso de error del servidor
        this.stopRefreshTokenTimer()
        this.clearAllTokens()
        return { error }
      })
  }

  public accessTokenExpired(): boolean {
    const tokenRemainingTime = this.getTokenRemainingTime();
    let expired = true;

    if (!!tokenRemainingTime) {
      const twoMinutes = 1000 * 60 * 2;
      expired = tokenRemainingTime < twoMinutes;
    }

    return expired;
  }

  public updatePassword = async ({ id, body }) => {
    return action
      .Patch({
        url: `/users/${id}/update-password`,
        body,
      })
      .then(response => {
        return { response }
      })
      .catch(error => {
        return { error }
      })
  }

  public cleanPassword = async ({ id, body }) => {
    return action
      .Patch({
        url: `/users/${id}/clean-password`,
        body,
      })
      .then(response => {
        return { response }
      })
      .catch(error => {
        return { error }
      })
  }

  public getTokenExpirationTime(): Date | undefined {
    const accessToken = cookie.get("GASAPP_TOKEN")

    if (accessToken) {
      const accessTokenJson = parseJwt(accessToken)
      const time = accessTokenJson.exp * 1000
      return new Date(time);
    }
  }

  public getTokenRemainingTime(): number {
    const expires = this.getTokenExpirationTime()
    let timeout = 0;

    if (expires) {
      timeout = expires.getTime() - Date.now() - 60 * 1000
    }

    return timeout
  }

  private startRefreshTokenTimer() {
    this.refreshTokenTimeout = setTimeout(() => this.refreshToken(), this.getTokenRemainingTime())
  }

  private stopRefreshTokenTimer() {
    clearTimeout(this.refreshTokenTimeout)
  }

  private clearAllTokens() {
    // Limpiar cookies de manera completa para todos los dominios y rutas posibles
    const domains = [window.location.hostname, `.${window.location.hostname}`]
    const paths = ['/', '/app', '/api']
    const cookies = ['GASAPP_TOKEN', 'GASAPP_REFRESH_TOKEN']

    domains.forEach(domain => {
      paths.forEach(path => {
        cookies.forEach(cookieName => {
          // Limpiar para el dominio específico
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=${domain}`
          // Limpiar sin dominio específico
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`
        })
      })
    })

    // Limpiar también cualquier token que pueda estar en localStorage o sessionStorage
    try {
      localStorage.removeItem('GASAPP_TOKEN')
      localStorage.removeItem('GASAPP_REFRESH_TOKEN')
      sessionStorage.removeItem('GASAPP_TOKEN')
      sessionStorage.removeItem('GASAPP_REFRESH_TOKEN')
    } catch (error) {
      console.warn('Error clearing storage tokens:', error)
    }
  }

  public sendRecoverPassword(payload) {
    return action
      .Post({
        url: '/auth/password-recovery',
        body: payload,
      })
      .then(response => {
        return { response }
      })
      .catch(error => {
        return { error }
      })
  }

  public resetPassword(payload) {
    return action
      .Post({
        url: '/auth/account-recovery',
        body: payload,
      })
      .then(response => {
        return { response }
      })
      .catch(error => {
        return { error }
      })
  }

  public changePassword(payload) {
    return action
      .Post({
        url: '/auth/change-password',
        body: payload,
      })
      .then(response => {
        return { response }
      })
      .catch(error => {
        return { error }
      })
  }

  /**
   * Método público para limpiar todos los tokens manualmente
   * Útil para casos de emergencia o cuando se detecta una sesión comprometida
   */
  public forceCleanupTokens() {
    this.stopRefreshTokenTimer()
    this.accessToken = null
    this.refreshAcessToken = null
    this.tokenInformation = null
    this.refreshAcessTokenInformation = null
    this.refreshTokenTimeout = null
    this.clearAllTokens()
  }

  /**
   * Método para limpiar completamente el estado de autenticación
   * Incluye tokens, usuario actual y cualquier dato relacionado
   */
  public forceCleanupAll() {
    this.forceCleanupTokens()

    // Limpiar también cualquier dato del usuario que pueda estar en el navegador
    try {
      // Limpiar datos específicos del usuario
      localStorage.removeItem('currentUser')
      localStorage.removeItem('user')
      localStorage.removeItem('userProfile')
      sessionStorage.removeItem('currentUser')
      sessionStorage.removeItem('user')
      sessionStorage.removeItem('userProfile')

      // Limpiar cualquier cookie relacionada con el usuario
      const userRelatedCookies = ['USER_SESSION', 'USER_DATA', 'USER_PROFILE']
      userRelatedCookies.forEach(cookieName => {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
      })
    } catch (error) {
      console.warn('Error clearing user data:', error)
    }
  }
}

const auth = new Auth()
export default auth
